import type { GanttScale } from "./ganttScale";
import { shiftISO } from "./ganttScale";

/**
 * Pure drag maths for the Gantt bars: pixels dragged in, a new date pair out.
 *
 * Separate from ganttScale so the two can be tested apart, and pure for the
 * same reason: a drag that lands one day off looks entirely plausible on
 * screen. The component owns pointer capture and the preview; this owns what
 * the dates become.
 *
 * ── Why not @dnd-kit ──
 * The Kanban uses it, and it is the wrong tool here. dnd-kit resolves a drag
 * into a *droppable* through collision detection and hands back a delta plus
 * an `over` id — a discrete model. A Gantt bar tracks the cursor continuously
 * and commits a date computed from total dx, with no droppable in the picture,
 * so we would use `useDraggable` purely for its transform and discard the
 * whole droppable half. Three grab regions per bar (body + two edges) would
 * also mean three draggables per row. The one genuinely useful piece — a
 * pixel threshold before a drag starts — is `DRAG_ACTIVATION_PX` below.
 */

/** Pointer travel before a press becomes a drag. Matches the Kanban's
 *  PointerSensor activation distance, and for the same reason: a bar must
 *  still take a plain click to open the detail panel. */
export const DRAG_ACTIVATION_PX = 5;

export type GanttDragMode = "move" | "resize-start" | "resize-end";

/** The dates a bar is drawn from. Either may be null — a one-sided issue is
 *  schedulable, and dragging its free edge is what sets the other end. */
export interface GanttDates {
  start: string | null;
  due: string | null;
}

/** Whole days travelled, from raw pixels. Rounds, so a bar snaps to the
 *  nearest day column as the cursor crosses its midpoint. */
export function deltaDays(scale: GanttScale, dxPx: number): number {
  return Math.round(dxPx / scale.pxPerDay);
}

/**
 * The dates after dragging `dates` by `dxPx` in `mode`.
 *
 * Invariants, both load-bearing:
 *
 *  - **move preserves duration** — it shifts whichever ends exist by the same
 *    number of days, so a one-sided issue stays one-sided.
 *  - **resize can never invert a bar.** Each edge clamps against the other, so
 *    a bar bottoms out at a single day. The database permits an inverted pair
 *    (the other editors write one date at a time and would otherwise deadlock
 *    on the constraint), so the Gantt is where the invariant is actually
 *    enforced — it is the one surface that writes both ends at once.
 *
 * Returns only the fields that CHANGED, so the caller's PATCH carries a body
 * that matches the gesture: a body drag writes both dates, an edge drag one.
 */
export function applyDrag(
  scale: GanttScale,
  dates: GanttDates,
  mode: GanttDragMode,
  dxPx: number
): Partial<{ start_date: string | null; due_date: string | null }> {
  const d = deltaDays(scale, dxPx);
  if (d === 0) return {};

  if (mode === "move") {
    const patch: Partial<{ start_date: string; due_date: string }> = {};
    if (dates.start) patch.start_date = shiftISO(dates.start, d);
    if (dates.due) patch.due_date = shiftISO(dates.due, d);
    return patch;
  }

  if (mode === "resize-start") {
    // With no start date yet, the left edge sits on the due date; dragging it
    // is how the issue gains a start. Clamp so it never passes the due date.
    const from = dates.start ?? dates.due;
    if (!from) return {};
    let next = shiftISO(from, d);
    if (dates.due && next > dates.due) next = dates.due;
    return next === dates.start ? {} : { start_date: next };
  }

  // resize-end — the mirror image.
  const from = dates.due ?? dates.start;
  if (!from) return {};
  let next = shiftISO(from, d);
  if (dates.start && next < dates.start) next = dates.start;
  return next === dates.due ? {} : { due_date: next };
}

/** The dates a bar should be DRAWN from mid-drag, so the preview matches what
 *  a release would commit. Same maths, applied to the pair rather than a diff. */
export function previewDates(
  scale: GanttScale,
  dates: GanttDates,
  mode: GanttDragMode,
  dxPx: number
): GanttDates {
  const patch = applyDrag(scale, dates, mode, dxPx);
  return {
    start: "start_date" in patch ? (patch.start_date ?? null) : dates.start,
    due: "due_date" in patch ? (patch.due_date ?? null) : dates.due,
  };
}
